<template>
  <div class="mb-5 pb-5">
    <div class="alert alert-info">
      <i class="fas fa-info-circle"></i>
      Dados disponíveis apenas para envios realizados após a atualização do sistema.
      Envios anteriores não são contabilizados.
    </div>

    <div class="row mt-3">
      <div class="col-md-4">
        <Autocomplete
          label="Pipeline"
          name="pipeline"
          v-model="filters.pipeline"
          :items="pipelineList"
          :multiple="true"
          placeholder="Todos os pipelines"
        />
      </div>
      <div class="col-md-3">
        <Autocomplete
          label="Template"
          name="template"
          v-model="filters.template"
          :items="templateList"
          placeholder="Todos os templates"
        />
      </div>
      <div class="col-md-2">
        <Input
          label="Data Inicial"
          v-model="filters.data_ini"
          name="data_ini"
          type="date"
        />
      </div>
      <div class="col-md-2">
        <Input
          label="Data Final"
          v-model="filters.data_fim"
          name="data_fim"
          type="date"
        />
      </div>
      <div class="col-md-1 col-12 d-flex align-items-end filter-buttons mb-3">
        <button type="button" class="btn btn-success btn-sm mr-1" @click="filtrar">
          <Icon type="fas" icon="search" color="white" />
        </button>
        <button type="button" class="btn btn-cancel btn-sm" @click="limpar">
          <Icon type="fas" icon="times" title="Limpar" />
        </button>
      </div>
    </div>

    <template v-if="filtrou">
      <section class="mt-4">
        <h5 class="mb-3">
          <i class="fas fa-comments text-primary"></i>
          Envios gerais (Chat / Tarefa / Contrato / Ativo)
        </h5>
        <div class="row mb-3">
          <div class="col-md-4" v-for="card in chatKpiCards" :key="'chat-' + card.label">
            <div class="card border-left-primary shadow-sm h-100 py-2">
              <div class="card-body">
                <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">{{ card.label }}</div>
                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ card.value }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="tabela-relatorio">
          <DataTable
            name="Templates — Envios gerais"
            :headers="chatHeaders"
            :data="chatData"
            :pesquisa="false"
          />
        </div>
      </section>

      <section class="mt-5">
        <h5 class="mb-3">
          <i class="fas fa-bullhorn text-success"></i>
          Campanhas
        </h5>
        <div class="row mb-3">
          <div class="col-md-4" v-for="card in campanhaKpiCards" :key="'campanha-' + card.label">
            <div class="card border-left-success shadow-sm h-100 py-2">
              <div class="card-body">
                <div class="text-xs font-weight-bold text-success text-uppercase mb-1">{{ card.label }}</div>
                <div class="h5 mb-0 font-weight-bold text-gray-800">{{ card.value }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="tabela-relatorio">
          <DataTable
            name="Templates — Campanhas"
            :headers="campanhaHeaders"
            :data="campanhaData"
            :pesquisa="false"
          />
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import api from '@/api';
import moment from 'moment';

import Icon from '../ui/Icon.vue';
import Autocomplete from '../ui/Autocomplete.vue';
import Input from '../ui/Input.vue';
import DataTable from '../ui/DataTable.vue';

const defaultFilters = () => ({
  pipeline: [],
  template: null,
  data_ini: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  data_fim: moment().format('YYYY-MM-DD'),
});

export default {
  name: 'RelatorioEngajamentoTemplates',
  components: {
    Icon,
    Autocomplete,
    Input,
    DataTable,
  },
  data() {
    return {
      filtrou: false,
      filters: defaultFilters(),
      pipelineList: [],
      templateList: [],
      chatData: [],
      campanhaData: [],
      chatTotais: { enviados: 0, respondidos: 0, taxa: 0 },
      campanhaTotais: { enviados: 0, respondidos: 0, taxa: 0 },
    };
  },
  computed: {
    chatHeaders() {
      return [
        { text: 'Template', value: 'template_nome' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Enviados', value: 'enviados', align: 'right', total: true, precision: 0 },
        { text: 'Respondidos', value: 'respondidos', align: 'right', total: true, precision: 0 },
        { text: 'Taxa (%)', value: 'taxa', align: 'right', media: true, precision: 2 },
      ];
    },
    campanhaHeaders() {
      return [
        { text: 'Template', value: 'template_nome' },
        { text: 'Campanha', value: 'campanha_nome' },
        { text: 'Enviados', value: 'enviados', align: 'right', total: true, precision: 0 },
        { text: 'Respondidos', value: 'respondidos', align: 'right', total: true, precision: 0 },
        { text: 'Taxa (%)', value: 'taxa', align: 'right', media: true, precision: 2 },
      ];
    },
    chatKpiCards() {
      return [
        { label: 'Enviados', value: this.chatTotais.enviados },
        { label: 'Respondidos', value: this.chatTotais.respondidos },
        { label: 'Taxa de resposta', value: `${this.chatTotais.taxa}%` },
      ];
    },
    campanhaKpiCards() {
      return [
        { label: 'Enviados', value: this.campanhaTotais.enviados },
        { label: 'Respondidos', value: this.campanhaTotais.respondidos },
        { label: 'Taxa de resposta', value: `${this.campanhaTotais.taxa}%` },
      ];
    },
  },
  mounted() {
    this.getPipelines();
    this.getTemplates();
    this.filtrar();
  },
  methods: {
    getPipelines() {
      api.get('/painel/oportunidades-pipelines', { params: { data: 1 } })
        .then(response => {
          this.pipelineList = response.data.map(pipeline => ({
            id: pipeline.id,
            value: pipeline.value,
          }));
        })
        .catch(error => {
          console.error('Erro ao buscar pipelines:', error);
        });
    },
    getTemplates() {
      api.get('/painel/relatorio-templates/templates')
        .then(response => {
          this.templateList = (response.data ?? []).map(template => ({
            id: template.id,
            value: template.value,
          }));
        })
        .catch(() => {
          this.templateList = [];
        });
    },
    limpar() {
      this.filters = defaultFilters();
      this.filtrar();
    },
    async filtrar() {
      $('#overlayer-loading').addClass('show-overlayer');

      const params = {
        pipeline: this.filters.pipeline.map(p => p.id),
        data_ini: this.filters.data_ini,
        data_fim: this.filters.data_fim,
      };

      if (this.filters.template?.id) {
        params.template_id = this.filters.template.id;
      }

      await api.get('/painel/relatorio-templates/metricas', { params })
        .then(response => {
          const chat = response.data.chat ?? {};
          const campanha = response.data.campanha ?? {};

          this.chatTotais = chat.totais ?? { enviados: 0, respondidos: 0, taxa: 0 };
          this.campanhaTotais = campanha.totais ?? { enviados: 0, respondidos: 0, taxa: 0 };
          this.chatData = chat.por_template ?? [];
          this.campanhaData = campanha.por_template ?? [];
        })
        .catch(error => {
          let errorMessage = 'Erro ao buscar relatório de templates';
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          if (error.response?.data?.errors) {
            Object.values(error.response.data.errors).forEach(item => {
              errorMessage = errorMessage.concat('</br>', item);
            });
          }
          window.showToastDanger('body', errorMessage);
        })
        .finally(() => {
          this.filtrou = true;
          $('#overlayer-loading').removeClass('show-overlayer');
        });
    },
  },
};
</script>

<style scoped>
.border-left-primary {
  border-left: 0.25rem solid #4e73df !important;
}
.border-left-success {
  border-left: 0.25rem solid #1cc88a !important;
}

/* O DataTable renderiza container-fluid/row/col próprios; zera os gutters
   internos para a tabela ocupar 100% e alinhar com os demais blocos. */
.tabela-relatorio ::v-deep .container-fluid {
  padding-left: 0;
  padding-right: 0;
}
.tabela-relatorio ::v-deep .container-fluid > .row {
  margin-left: 0;
  margin-right: 0;
}
.tabela-relatorio ::v-deep .container-fluid > .row > .col {
  padding-left: 0;
  padding-right: 0;
}
</style>
